/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { TChannelsType } from "@tivocorp/data-domain-lib";

import { Log } from "../../model/logging";
import { ChannelsInteractor } from "../../model/interactor";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import { PlaybackContentType } from "../../model/videoplayer/enums";
import {
  DEFAULT_ELIGIBILITY,
  DEFAULT_TRICKPLAY_CONFIG
} from "../../model/videoplayer/trickplay";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import { selectActiveScreen } from "../screens";
import { ScreenControllerTypes } from "../../model/navigation";

const TAG: string = "channelNumberEntrySlice";

/** Maximum number of digits a user may enter for a channel number. */
export const CHANNEL_NUMBER_MAX_LENGTH: number = 4;

/**
 * Inactivity window (ms) after the last digit press before the accumulated
 * channel number is submitted automatically.
 */
export const CHANNEL_NUMBER_ENTRY_TIMEOUT_MS: number = 2 * 1000;

// ── Module-level timer (non-serializable — must NOT live in Redux state) ──
let entryTimeoutId: ReturnType<typeof setTimeout> | null = null;

// ── State ──

export interface ChannelNumberEntryState {
  /** Digits accumulated so far (e.g. "123"). */
  channelNumberEntered: string;
  /** Whether direct-dial entry mode is currently active. */
  isEnteringChannelNumber: boolean;
  /** Non-empty when a tune attempt fails (e.g. channel not found). */
  error: string;
}

const INITIAL_STATE: ChannelNumberEntryState = {
  channelNumberEntered: "",
  isEnteringChannelNumber: false,
  error: ""
};

// ── Slice ──

const channelNumberEntrySlice = createSlice({
  name: "channelNumberEntry",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Append a single validated digit to the accumulated channel number.
     * Length guard is enforced by the caller thunk; reducer trusts the input.
     */
    appendDigit(
      state: ChannelNumberEntryState,
      action: PayloadAction<string>
    ): void {
      state.channelNumberEntered += action.payload;
      state.error = "";
    },

    /** Activate direct-dial entry mode. */
    enableChannelNumberEntry(state: ChannelNumberEntryState): void {
      state.isEnteringChannelNumber = true;
      state.error = "";
    },

    /** Clear all accumulated digits and deactivate entry mode. */
    clearChannelNumberEntry(state: ChannelNumberEntryState): void {
      state.channelNumberEntered = "";
      state.isEnteringChannelNumber = false;
      state.error = "";
    },

    /** Record a tune error (e.g. channel not found). */
    setChannelNumberEntryError(
      state: ChannelNumberEntryState,
      action: PayloadAction<string>
    ): void {
      state.error = action.payload;
    }
  }
});

export const {
  appendDigit,
  enableChannelNumberEntry,
  clearChannelNumberEntry,
  setChannelNumberEntryError
} = channelNumberEntrySlice.actions;

export const channelNumberEntryReducer = channelNumberEntrySlice.reducer;

// ── Selectors ──

export const selectChannelNumberEntered = (state: UnityRootState): string =>
  state.channelNumberEntry.channelNumberEntered;

export const selectIsEnteringChannelNumber = (state: UnityRootState): boolean =>
  state.channelNumberEntry.isEnteringChannelNumber;

export const selectChannelNumberEntryError = (state: UnityRootState): string =>
  state.channelNumberEntry.error;

// ── Thunks ──

/**
 * Resolves the nearest channel at or above `channelNumber` using
 * `fetchNextChannelsFromChannelNumber` (maps to `fetchNextChannelsFromChannelNumberV2`
 * internally), then starts playback.
 *
 * Resolution rules (matching Kaveri's `fetchNextChannelsFromChannelNumberV2`):
 * 1. Exact match in the channel index → use it.
 * 2. No exact match → first channel whose number is greater than the entered value.
 * 3. No greater channel exists → wraps to the last channel in the list.
 *
 * `channelsType` controls which channel list is searched (all / subscribed / favorite).
 * Clears entry state on success; sets an error message on failure.
 */
export const submitChannelNumber =
  (channelsType: TChannelsType) =>
  async (
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ): Promise<void> => {
    const { channelNumberEntered } = getState().channelNumberEntry;

    if (!channelNumberEntered) {
      Log.d(TAG, "submitChannelNumber() — nothing to submit");
      dispatch(clearChannelNumberEntry());
      return;
    }

    Log.i(TAG, "submitChannelNumber() — tuning to: " + channelNumberEntered);

    try {
      const interactor = new ChannelsInteractor();

      // fetchNextChannelsFromChannelNumber resolves the nearest channel at or
      // above the entered number — exact match first, then next-greater, then
      // last channel (wrap-around). A limit of 1 returns only that channel.
      const result = await interactor.fetchNextChannelsFromChannelNumber(
        channelNumberEntered,
        channelsType,
        1
      );

      const channel = result.channels[0];
      if (!channel) {
        const msg = "No channel found for: " + channelNumberEntered;
        Log.w(TAG, "submitChannelNumber() — " + msg);
        dispatch(setChannelNumberEntryError(msg));
        dispatch(clearChannelNumberEntry());
        return;
      }

      const activeScreen = selectActiveScreen(getState());
      if (activeScreen === ScreenControllerTypes.FULL_SCREEN_PLAYBACK) {
        const vpc = VideoPlaybackController.getInstance();

        // End any in-progress trick-play session before tuning — the channel
        // is about to be fully replaced, so this is a clean stop (no seek/play)
        // rather than a cancel/confirm that would do wasted seek work.
        if (vpc.isTrickPlayActive()) {
          vpc.trickPlayStop();
        }

        vpc.startPlayback({
          channel,
          playbackContentType: PlaybackContentType.LIVE
        });

        // Re-initialise trick-play after channel change — startPlayback() calls
        // stop() which destroys the previous TrickPlayManager.
        vpc.initTrickPlay(DEFAULT_TRICKPLAY_CONFIG, DEFAULT_ELIGIBILITY);
        Log.i(
          TAG,
          "submitChannelNumber() — playback started for channel: " +
            String(channel.channelNumber)
        );
      }
      dispatch(clearChannelNumberEntry());
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Channel not found";
      Log.e(TAG, "submitChannelNumber()", error);
      dispatch(setChannelNumberEntryError(message));
      dispatch(clearChannelNumberEntry());
    }
  };

/**
 * Handles a single digit key press during full-screen playback.
 *
 * On each press:
 * 1. Activates entry mode (first digit only).
 * 2. Appends the digit (up to `CHANNEL_NUMBER_MAX_LENGTH`).
 * 3. Resets the inactivity timeout — when it fires the accumulated number
 *    is submitted automatically.
 * 4. If max length is reached after appending, submits immediately without waiting for timeout.
 */
export const handleDigitKeyPressed =
  (digit: string, channelsType: TChannelsType) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const { channelNumberEntered, isEnteringChannelNumber } =
      getState().channelNumberEntry;

    if (!isEnteringChannelNumber) {
      dispatch(enableChannelNumberEntry());
    }

    if (channelNumberEntered.length >= CHANNEL_NUMBER_MAX_LENGTH) {
      Log.w(
        TAG,
        "handleDigitKeyPressed() — max length reached, ignoring: " + digit
      );
      return;
    }

    dispatch(appendDigit(digit));
    Log.d(TAG, "handleDigitKeyPressed() — digit: " + digit);

    // Cancel any pending inactivity timeout
    if (entryTimeoutId !== null) {
      clearTimeout(entryTimeoutId);
      entryTimeoutId = null;
    }

    const newLength = channelNumberEntered.length + 1;

    if (newLength > CHANNEL_NUMBER_MAX_LENGTH) {
      // Max digits reached — submit after timeout
      dispatch(submitChannelNumber(channelsType));
      return;
    }

    // Schedule inactivity-triggered submit
    entryTimeoutId = setTimeout((): void => {
      entryTimeoutId = null;
      Log.d(TAG, "handleDigitKeyPressed() — inactivity timeout, submitting");
      dispatch(submitChannelNumber(channelsType));
    }, CHANNEL_NUMBER_ENTRY_TIMEOUT_MS);
  };

/**
 * Cancels any pending inactivity timeout and clears entry state.
 * Call on component unmount or when navigating away from full-screen playback.
 */
export const clearChannelNumberEntryTimers = (): void => {
  if (entryTimeoutId !== null) {
    clearTimeout(entryTimeoutId);
    entryTimeoutId = null;
  }
};
